/* eslint-disable */
import { differenceWith, drop, dropLast, omit, isEmpty, sortBy, prop, last, union, difference, equals } from 'ramda';
import cloneDeep from 'lodash.clonedeep';
import {
  isSameDay,
  startOfDay,
  isWithinInterval,
  differenceInMilliseconds,
  differenceInHours,
  max as maxDate,
  addDays,
  closestIndexTo,
  subDays,
  areIntervalsOverlapping,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { v4 } from 'uuid';
import { SeriesOptionsType, XAxisOptions, YAxisOptions } from 'highcharts';

import {
  SchedulingAction,
  ChartScheduleElement,
  ScheduleElement,
  SchedulingState,
  SchedulingStateToHistory,
  ActionType,
  ScheduleHistory,
  DaySettings,
  QueuesWithColors,
  NearestSurroundingDaysElements,
  ProductionChartScheduleElement,
  SchedulingChartType,
} from '../types';
import {
  elementInRange,
  getScheduleElementsForDateRange,
  getWorkingAgents,
  mapChartData,
  mapInitialChartData,
  mapNotAssignedAgentSeriesData,
  mapProductionScheduleData,
} from '../utils/mapChartData';
import { Action } from './actions';
import {
  CHART_BOTTOM_MARGIN,
  CHART_HOUR_INTERVAL_WIDTH,
  defaultDaySettings,
  defaultAgentDaySettings,
  SCHEDULING_CHART_COLOR_PALETTE,
  initialSchedulingState,
  MIN_5,
} from '../config';
import { STATIC_ELEMENT_HEIGHT } from '../../../constants';
import { convertFieldsEndOfDateRange, createEndOfDateRange } from '../../../utils/dateRange';
import { customSort } from '../../../utils/sort';
import { ForecastType, Queue, ScheduleAgentDaySettings, Skill } from '../../../graphql/components';
import { CommonScheduleElement, SortOrder, Values } from '../../../types';
import { recordBy, recordFromIds } from '../../../utils/array';
import { startOfDayInZone } from '../../../utils/date';
import { getDefaultDaySettings } from '../utils/formatUtils';
import { Agent } from '../../Agents/types';

export const getAgentElementsWithCorrectPosition = <T extends ChartScheduleElement>(
  scheduleElements: T[],
  agentsIds: string[]
): T[] =>
  /**
   * Previous implementation was filtering out elements where agent ID was not present in agentIds param.
   * When team structure is applied, only elements from supervised agents are visible, but other elements
   * (from non supervised teams) are present in schedule data, so that correct work contribution is calculated.
   *
   * Calculation of work contribution HAS to be performed on all elements. Therefore elements filtering cannot be
   * performed here, and elements that are not visible will have y-index of "-1".
   */
  scheduleElements.map((element) => ({
    ...element,
    y: agentsIds.indexOf(element.agentId),
  }));

export const createElementToPaste = (
  dateToPaste: number,
  endOfDateToPaste: number,
  scheduleStartDateTime: number,
  scheduleEndDateTime: number,
  element: ChartScheduleElement,
  agentId: string = element.agentId,
  selected = false,
  viewedTimezone: string,
  scheduleTimezone: string,
  days: Date[],
  y?: number
): ChartScheduleElement | null => {
  // convert dates from UTC to selected time zone
  const elementStartZoned = utcToZonedTime(new Date(element.start), viewedTimezone);
  const elementEndZoned = utcToZonedTime(new Date(element.end), viewedTimezone);

  // calculate new range in selected time zone
  let start =
    dateToPaste +
    differenceInMilliseconds(elementStartZoned, startOfDayInZone(new Date(element.start), viewedTimezone));
  let end =
    dateToPaste + differenceInMilliseconds(elementEndZoned, startOfDayInZone(new Date(element.end), viewedTimezone));

  // convert dates back to UTC
  start = zonedTimeToUtc(new Date(start), viewedTimezone).getTime();
  end = zonedTimeToUtc(new Date(end), viewedTimezone).getTime();

  if (end <= start) {
    // element should be added at the end of day, but the end date was not properly calculated yet
    end = addDays(end, 1).getTime();
  }

  if (start < dateToPaste) {
    // element cannot overflow to previous day
    start = dateToPaste;
  }

  if (end > endOfDateToPaste) {
    // element cannot overflow to next day
    end = endOfDateToPaste;
  }

  if (start < scheduleStartDateTime) {
    if (end <= scheduleStartDateTime) {
      // whole element's range is out of schedule range
      return null;
    }

    // start of element's range needs to be cropped
    start = scheduleStartDateTime;
  }

  if (end > scheduleEndDateTime) {
    if (start >= scheduleEndDateTime) {
      // whole element's range is out of schedule range
      return null;
    }

    // end of element's range needs to be cropped
    end = scheduleEndDateTime;
  }

  // Setting new key, target agentId and proper date
  const id = v4();
  return {
    ...element,
    agentId,
    start,
    end,
    selected,
    y,
    key: id,
    id,
    dayIndexes: element.shift ? getDayIndexesForElement(start, end, scheduleTimezone, days) : [],
  };
};

const unselectElement = (element: ChartScheduleElement) => ({
  ...element,
  selected: false,
});

export const getElementsKeysWithFlag = <T extends CommonScheduleElement, K extends keyof T>(
  elements: T[],
  flag: K,
  // @ts-ignore - this assignment should be type safe!
  defaultValue?: T[K] = true
) => elements.filter((element) => element[flag] === defaultValue).map(({ key }) => key);

export const cropElementsOuterRangeOverflow =
  (start: number, end: number, timezone: string, days: Date[]) => (defaultElement: ChartScheduleElement) => {
    const element = cloneDeep(defaultElement);

    if (element.start < start) {
      // crop element's start date as it would overflow desired start range
      element.start = start;
    }

    if (element.end > end) {
      // crop element's end date as it would overflow desired end range
      element.end = end;
    }

    element.dayIndexes = element.shift ? getDayIndexesForElement(element.start, element.end, timezone, days) : [];

    return element;
  };

export const cropElementsInnerRangeOverlap =
  (start: number, end: number, timezone: string, days: Date[]) => (defaultElement: ChartScheduleElement) => {
    const element = cloneDeep(defaultElement);

    if (element.end > end && element.start < end) {
      // crop element's start date as it would overlap with desired range
      element.start = end;
    }

    if (element.start < start && element.end > start) {
      // crop element's end date as it would overlap with desired range
      element.end = start;
    }

    element.dayIndexes = element.shift ? getDayIndexesForElement(element.start, element.end, timezone, days) : [];

    return element;
  };

export const getDayIndexesForElement = (start: number, end: number, timeZone: string, days: Date[]): number[] => {
  const startIndex = closestIndexTo(startOfDayInZone(new Date(start + 1), timeZone), days) ?? 0;
  const endIndex = closestIndexTo(startOfDayInZone(new Date(end - 1), timeZone), days) ?? 0;

  const dayIndexes = [];
  for (let i = startIndex; i <= endIndex; i += 1) {
    dayIndexes.push(i); // index for every day between start <-> end
  }

  return dayIndexes;
};

const computeYAxisLineWidth = (agentsIds: string[]) => (agentsIds.length > 1 ? 1 : 0);

export const computeChartsHeight = (agentsIds: string[]) =>
  agentsIds.length * STATIC_ELEMENT_HEIGHT + CHART_BOTTOM_MARGIN;

export const findNearestElementsWithinSurroundingDays: (
  elements: ChartScheduleElement[],
  currentDayStart: number,
  currentDayEnd: number
) => NearestSurroundingDaysElements = (elements, currentDayStart, currentDayEnd) => {
  const previousDayStart = subDays(currentDayStart, 1).getTime();
  const nextDayEnd = addDays(currentDayEnd, 1).getTime();

  return elements.reduce<NearestSurroundingDaysElements>(
    (nearestElements, element) => {
      if (
        !isWithinInterval(element.end, { start: previousDayStart + 1, end: currentDayStart }) &&
        !isWithinInterval(element.start, { start: currentDayEnd, end: nextDayEnd - 1 })
      ) {
        // do not consider elements that are fully in other days than previous / next day
        return nearestElements;
      }

      if (element.end <= currentDayStart && (nearestElements.previousDay[element.agentId]?.end ?? 0 < element.end)) {
        // store closer element to the current day from start
        nearestElements.previousDay[element.agentId] = element;
      }

      if (
        element.start >= currentDayEnd &&
        (!nearestElements.nextDay[element.agentId] || nearestElements.nextDay[element.agentId]!.start > element.start)
      ) {
        // store closer element to the current day from end
        nearestElements.nextDay[element.agentId] = element;
      }

      return nearestElements;
    },
    {
      previousDay: {},
      nextDay: {},
    }
  );
};

/**
 * filtered supervised agents need to contain also not assigned agents if present
 * @param agentIds ids of agents present within schedule
 * @param supervisedAgents record object containing data of supervised agents
 * @param notAssignedAgentIds ids of not assigned agents (possibly present for edit of schedule)
 * @returns ids of supervised and not assigned agents
 */
export const filterSupervisedAgentIds = (
  agentIds: string[],
  supervisedAgents: Record<string, Agent>,
  notAssignedAgentIds: string[]
) => {
  return agentIds.filter((agentId) => supervisedAgents[agentId] || notAssignedAgentIds.includes(agentId));
};

export const hasWorkContributionData = ({ queueIds, schedulingChartState }: SchedulingState) => {
  if (!queueIds.length) {
    return false;
  }

  return queueIds.some((queueId) => {
    if (!schedulingChartState || !schedulingChartState.dataPerQueue) {
      return false;
    }

    const queueData = schedulingChartState.dataPerQueue[queueId];
    if (!queueData) {
      return false;
    }

    return (
      queueData.scheduledStaff.some(([_, value]) => value !== 0) ||
      queueData.requiredStaff.some(([_, value]) => value !== -1)
    );
  });
};

export const createStateToStoreInHistory = (state: SchedulingState) => {
  /**
   * Is vital to remove unnecessary information out of History object to deliver optimal performance.
   * Please do check this with every data structure changes
   */

  const newState: SchedulingStateToHistory = {
    chartState: {
      plotOptions: {
        series: {
          allowPointSelect: state.chartState.plotOptions?.series?.allowPointSelect,
          dragDrop: state.chartState.plotOptions?.series?.dragDrop,
          enableMouseTracking: state.chartState.plotOptions?.series?.enableMouseTracking,
          lineWidth: state.chartState.plotOptions?.series?.lineWidth,
        },
      },
      xAxis: state.chartState.xAxis,
      time: state.chartState.time,
    },
    daysSettings: state.daysSettings,
    initialQueuesStaffingData: state.initialQueuesStaffingData,
    queuesStaffingOfProductionElementsData: state.queuesStaffingOfProductionElementsData,
    inactiveAgentIdsWithScheduleElement: state.inactiveAgentIdsWithScheduleElement,
    isDirty: state.isDirty,
    nearestSurroundingDaysElements: state.nearestSurroundingDaysElements,
    notAssignedAgentIdsWithScheduleElement: state.notAssignedAgentIdsWithScheduleElement,
    pickedDayIndex: state.pickedDayIndex,
    pinnedScheduleElementsIds: state.pinnedScheduleElementsIds,
    pinnedUsedScheduleElementsIds: state.pinnedUsedScheduleElementsIds,
    selectedAgentIds: state.selectedAgentIds,
    selectedAgentIdsSorted: state.selectedAgentIdsSorted,
    selectedScheduleElementsIds: state.selectedScheduleElementsIds,
    usedScheduleElements: state.usedScheduleElements,
    usedScheduleElementsWithinCurrentDay: state.usedScheduleElementsWithinCurrentDay,
    usePrecalculatedQueuesStaffing: state.usePrecalculatedQueuesStaffing,
    viewedTimezone: state.viewedTimezone,
    visibleAgentIds: state.visibleAgentIds,
    visibleSupervisedAgentIds: state.visibleSupervisedAgentIds,
    visibleAgentIdsInViewPort: state.visibleAgentIdsInViewPort,
    autoScheduleOptions: state.autoScheduleOptions,
    autoScheduleSelectedRange: state.autoScheduleSelectedRange,
    nearestSurroundingDaysProductionElements: state.nearestSurroundingDaysProductionElements,
    productionScheduleElementsWithinCurrentDay: state.productionScheduleElementsWithinCurrentDay,
    schedulingChartType: state.schedulingChartType,
    forecastDataType: state.forecastDataType,
  };

  return cloneDeep(newState);
};

export const newStateWithHistory = (
  scheduleHistory: ScheduleHistory,
  newState: SchedulingState,
  action?: ActionType,
  historySizeLimit = 15
): SchedulingState => {
  const { schedulingStateHistory, positionInHistory } = scheduleHistory;
  const nextPositionInHistory = positionInHistory + 1;

  // When a user takes e.g. 4 steps back and does change, the change is now latest in the list of changes
  const positionDifference = schedulingStateHistory.length - (positionInHistory + 1);
  const droppedLastHistory = dropLast(positionDifference, schedulingStateHistory);

  // Deleting non essential properties
  const newStateToHistory = createStateToStoreInHistory(newState);

  if (action) {
    newStateToHistory.actionType = action;
  }

  return {
    ...newState,
    scheduleHistory: {
      positionInHistory: nextPositionInHistory <= historySizeLimit ? nextPositionInHistory : historySizeLimit,
      schedulingStateHistory:
        nextPositionInHistory <= historySizeLimit
          ? [...droppedLastHistory, newStateToHistory]
          : [...drop(1, droppedLastHistory), newStateToHistory],
    },
  };
};

export const normalizeAgentsDaySettings = (data: ScheduleAgentDaySettings[]) =>
  data.reduce<DaySettings['agents']>((normalized, { agentId, remote }) => {
    return {
      ...normalized,
      [agentId]: {
        agentId,
        remote,
      },
    };
  }, {});

export const reducer: (state: SchedulingState, action: Action) => SchedulingState = (state, action) => {
  if (process.env.NODE_ENV === 'development') {
    console.debug(`Performing action with type -> ${action.type}`);
    console.debug(cloneDeep(action));
  }

  switch (action.type) {
    case SchedulingAction.COMPOSE_DATA: {
      const {
        activeAgents,
        agentsRecordObj,
        supervisedAgentsRecordObj,
        allAgentIdsSorted,
        daysSettings,
        deletedAgentsWithScheduleElement,
        inactiveAgentIdsWithScheduleElement,
        includeNotAssignedAgents,
        initialStaffingPerQueue = {},
        lockedAgentsIds,
        missingQueuesData,
        notAssignedAgents,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
        queueIds,
        queues,
        selectedAgentIds,
        shouldPreloadAllAgents,
        scheduleElements,
        skills,
        usedScheduleElements,
        usePrecalculatedQueuesStaffing,
      } = action.payload;

      const notAssignedAgentsIds = notAssignedAgents.map(({ id }) => id);
      const appliedNotAssignedAgentsIds = includeNotAssignedAgents ? notAssignedAgentsIds : [];

      let filteredSelectedAgentIds = selectedAgentIds;
      let filteredAllAgentIdsSorted = allAgentIdsSorted;
      let filteredUsedScheduleElements = usedScheduleElements;
      let filteredDaysSettings = daysSettings;

      /**
       * When creating new schedule, we should not count with contribution of not assigned agents, therefore all related data need to be cleaned up
       * (schedule items, selected agentIds, day settings).
       * However, when editing an existing schedule, we need to preserve these data!
       */
      if (!includeNotAssignedAgents && notAssignedAgentsIds) {
        filteredSelectedAgentIds = selectedAgentIds.filter((agentId) => !notAssignedAgentsIds.includes(agentId));
        filteredAllAgentIdsSorted = allAgentIdsSorted.filter((agentId) => !notAssignedAgentsIds.includes(agentId));
        filteredUsedScheduleElements = usedScheduleElements.filter(
          ({ agentId }) => !notAssignedAgentsIds.includes(agentId)
        );
        filteredDaysSettings = daysSettings.map((daySetting) => ({
          ...daySetting,
          agents: daySetting.agents.filter(({ agentId }) => !notAssignedAgentsIds.includes(agentId)),
        }));
      }

      const scheduleStart = (state.chartState.xAxis as XAxisOptions[])[0].min!;
      const scheduleEnd = (state.chartState.xAxis as XAxisOptions[])[0].max!;

      const unselectedUsedScheduleElements = filteredUsedScheduleElements.map(unselectElement);

      const deletedAgentIds = deletedAgentsWithScheduleElement.map(({ id }) => id);

      const productiveElementsIds = scheduleElements.reduce(
        (productiveElementIds: string[], element: ScheduleElement) => {
          if (element.productive) {
            productiveElementIds.push(element.id);
          }
          return productiveElementIds;
        },
        []
      );

      const skillsAllRecord = skills.reduce((skillsData, skill) => {
        skillsData[skill.id] = skill;

        return skillsData;
      }, {} as Record<string, Skill>);

      const { queuesRecord, usedSkillsRecord } = queues.reduce(
        (data, queue: Queue) => {
          if (queueIds.includes(queue.id)) {
            data.queuesRecord[queue.id] = queue;

            const { skillId } = queue;

            if (skillId && !data.usedSkillsRecord[skillId] && skillsAllRecord[skillId]) {
              data.usedSkillsRecord[skillId] = skillsAllRecord[skillId];
            }
          }

          return data;
        },
        {
          queuesRecord: cloneDeep(missingQueuesData),
          usedSkillsRecord: {},
        } as { queuesRecord: Record<string, Queue>; usedSkillsRecord: Record<string, Skill> }
      );

      const queueIdsSorted = customSort(queuesRecord, ['name'], SortOrder.ASCENDING, queueIds);

      const scheduleElementsData = getScheduleElementsForDateRange({
        startTimestamp: scheduleStart,
        endTimestamp: scheduleEnd,
        scheduleElements: getAgentElementsWithCorrectPosition(
          unselectedUsedScheduleElements,
          filteredAllAgentIdsSorted
        ),
      });

      const ganttElementsData = scheduleElementsData.filter(({ agentId }) => state.visibleAgentIdsInViewPort[agentId]);

      const numberOfHoursInCurrentSchedule = differenceInHours(scheduleEnd, scheduleStart);
      const chartWidth = CHART_HOUR_INTERVAL_WIDTH * numberOfHoursInCurrentSchedule;

      const sourceDaysSettings = shouldPreloadAllAgents
        ? getDefaultDaySettings(
            state.scheduleStartTime,
            convertFieldsEndOfDateRange(state.scheduleEndTime),
            activeAgents.map(({ id }) => ({ agentId: id }))
          )
        : filteredDaysSettings;

      const newDaysSettings = sourceDaysSettings.reduce<SchedulingState['daysSettings']>(
        (normalized, { dayIndex, agents: agentsDaySettings }) => {
          return {
            ...normalized,
            [dayIndex]: {
              ...defaultDaySettings,
              dayIndex: Number(dayIndex),
              agents: normalizeAgentsDaySettings(agentsDaySettings),
            },
          };
        },
        {}
      );

      /**
       * missingQueuesData should be persisted in queues record object, so that mapChartData has access
       * to their channel type to be able to calculate work contribution properly
       */
      const queuesWithColors = cloneDeep(missingQueuesData) as QueuesWithColors;
      queueIdsSorted.forEach((queueId, index) => {
        queuesWithColors[queueId] = { ...queuesRecord[queueId], color: SCHEDULING_CHART_COLOR_PALETTE[index] };
      });

      const productionScheduleElementsWithinCurrentDayUpdated = getScheduleElementsForDateRange({
        startTimestamp: scheduleStart,
        endTimestamp: scheduleEnd,
        scheduleElements: getAgentElementsWithCorrectPosition(productionScheduleElements, filteredAllAgentIdsSorted),
      });

      const productionElementForGantt = productionScheduleElementsWithinCurrentDayUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const sortedSupervisedAgentIds = filterSupervisedAgentIds(
        filteredAllAgentIdsSorted,
        supervisedAgentsRecordObj,
        appliedNotAssignedAgentsIds
      );

      const newState: SchedulingState = {
        ...state,
        scheduleElements,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
        productiveElementsIds,
        selectedAgentIds: filteredSelectedAgentIds,
        lockedAgentsIds,
        initialQueuesStaffingData: initialStaffingPerQueue,
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsWithinCurrentDayUpdated,
        usedScheduleElements: unselectedUsedScheduleElements,
        usedScheduleElementsWithinCurrentDay: scheduleElementsData,
        inactiveAgentIdsWithScheduleElement: [...inactiveAgentIdsWithScheduleElement, ...deletedAgentIds],
        agents: agentsRecordObj,
        supervisedAgentsRecordObj,
        visibleSupervisedAgentIds: sortedSupervisedAgentIds,
        selectedAgentIdsSorted: filteredAllAgentIdsSorted,
        usePrecalculatedQueuesStaffing: usePrecalculatedQueuesStaffing,
        visibleAgentIds: filteredAllAgentIdsSorted,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            width: chartWidth,
            height: computeChartsHeight(filteredAllAgentIdsSorted),
          },
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              max: Math.max(0, sortedSupervisedAgentIds.length - 1),
              gridLineWidth: computeYAxisLineWidth(filteredAllAgentIdsSorted),
            },
          ],
          xAxis: [
            {
              ...(state.chartState.xAxis as XAxisOptions[])[0],
              min: scheduleStart,
              max: scheduleEnd,
            },
          ],
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                notAssignedAgentsIds,
                filteredAllAgentIdsSorted,
                scheduleStart,
                scheduleEnd
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionElementForGantt),
            },
          ] as SeriesOptionsType[],
        },
        daysSettings: newDaysSettings,
        nearestSurroundingDaysElements: findNearestElementsWithinSurroundingDays(
          unselectedUsedScheduleElements,
          scheduleStart,
          scheduleEnd
        ),
        notAssignedAgentIdsWithScheduleElement: appliedNotAssignedAgentsIds,
        queues: queuesWithColors,
        queueIds: queueIdsSorted,
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(unselectedUsedScheduleElements, 'pinned'),
        schedulingAction: SchedulingAction.COMPOSE_DATA,
        skills: usedSkillsRecord,
        ...(process.env.NODE_ENV === 'test' && {
          /**
           * We need to preselect agents into viewport for tests, as mocked viewport would not trigger onResizeHandler
           * on mount, which results in no reloadChartData being called at all.
           * If we added that in real usage, the chart would struggle with rendering all provided elements
           * (some of them wouldn't be present).
           */
          visibleAgentIdsInViewPort: recordFromIds(sortedSupervisedAgentIds.slice(0, 11), true),
        }),
      };

      return {
        ...newState,
        scheduleHistory: {
          positionInHistory: 0,
          schedulingStateHistory: [createStateToStoreInHistory(newState)],
        },
      };
    }

    case SchedulingAction.RELOAD_SCHEDULE_AFTER_AUTO_SCHEDULE_FINISHED: {
      const { newInitialQueuesStaffing = {}, scheduleElements, scheduleElementsForGantt } = action.payload;

      const endTimestamp = (state.chartState.xAxis as XAxisOptions[])[0].max!;
      const startTimestamp = (state.chartState.xAxis as XAxisOptions[])[0].min!;

      const ganttElementsData = scheduleElementsForGantt.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const newState: SchedulingState = {
        ...state,
        usedScheduleElements: scheduleElements,
        usedScheduleElementsWithinCurrentDay: scheduleElementsForGantt,
        // as schedule can not be dirty in this state, pre-calculated contribution data should be used!
        schedulingChartState: mapInitialChartData({
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          // should these data be re-fetched as well? Could save of schedule have impact on other production schedules?
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          initialStaffingPerQueue: newInitialQueuesStaffing,
          queueIds: state.queueIds,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
        }),
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
              type: 'gantt',
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        initialQueuesStaffingData: newInitialQueuesStaffing,
        nearestSurroundingDaysElements: findNearestElementsWithinSurroundingDays(
          scheduleElements,
          startTimestamp,
          endTimestamp
        ),
        schedulingAction: SchedulingAction.RELOAD_SCHEDULE_AFTER_AUTO_SCHEDULE_FINISHED,
      };

      return {
        ...newState,
        scheduleHistory: {
          positionInHistory: 0,
          schedulingStateHistory: [createStateToStoreInHistory(newState)],
        },
      };
    }

    case SchedulingAction.UPDATE_DATE: {
      const { startSchedule, endSchedule } = action.payload;

      const scheduleElementsOfUpdatedDate = getScheduleElementsForDateRange({
        startTimestamp: startSchedule,
        endTimestamp: endSchedule,
        scheduleElements: getAgentElementsWithCorrectPosition(
          state.usedScheduleElements,
          state.visibleSupervisedAgentIds
        ),
      }).map(unselectElement);

      const ganttElementsData = scheduleElementsOfUpdatedDate.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const productionScheduleElementsOfUpdatedDate = getScheduleElementsForDateRange({
        startTimestamp: startSchedule,
        endTimestamp: endSchedule,
        scheduleElements: getAgentElementsWithCorrectPosition(
          state.productionScheduleElements,
          state.visibleSupervisedAgentIds
        ),
      });

      const productionScheduleElementsForGantt = productionScheduleElementsOfUpdatedDate.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const numberOfHoursInFollowingSchedule = differenceInHours(endSchedule, startSchedule);
      const updatedChartWidth = CHART_HOUR_INTERVAL_WIDTH * numberOfHoursInFollowingSchedule;

      const pickedDayIndex = closestIndexTo(startSchedule, state.days) ?? 0;

      const newState: SchedulingState = {
        ...state,
        pickedDayIndex,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            width: updatedChartWidth,
          },
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                state.visibleSupervisedAgentIds,
                startSchedule,
                endSchedule
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionScheduleElementsForGantt),
            },
          ] as SeriesOptionsType[],
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              gridLineWidth: computeYAxisLineWidth(state.visibleSupervisedAgentIds),
            },
          ],
          xAxis: [
            {
              ...(state.chartState.xAxis as XAxisOptions[])[0],
              min: startSchedule,
              max: endSchedule,
            },
          ],
        },
        nearestSurroundingDaysElements: findNearestElementsWithinSurroundingDays(
          state.usedScheduleElements,
          startSchedule,
          endSchedule
        ),
        nearestSurroundingDaysProductionElements: findNearestElementsWithinSurroundingDays(
          state.productionScheduleElements,
          startSchedule,
          endSchedule
        ),
        selectedScheduleElementsIds: [],
        // this state should be reset as new data have to be loaded before new state can be calculated
        schedulingChartState: {
          scheduleElementsContributionPerQueue: {},
          plotBandsData: [],
          yAxisTicks: [],
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(scheduleElementsOfUpdatedDate, 'pinned'),
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsOfUpdatedDate,
        usedScheduleElementsWithinCurrentDay: scheduleElementsOfUpdatedDate,
        schedulingAction: SchedulingAction.UPDATE_DATE,
        ...(state.schedulingChartType === SchedulingChartType.SLA && {
          isLoadingEstimatedServiceLevel: true, // pre-set loading state for SLA data to prevent flickering of SLA chart
        }),
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.ADD_ELEMENT: {
      const { newElement } = action.payload;
      const currentDaysElements = state.usedScheduleElementsWithinCurrentDay;
      const currentDaysProductionElements = state.productionScheduleElementsWithinCurrentDay;
      const scheduleElementsOnSameYAxis = currentDaysElements.filter((data) => data.y === newElement.y);
      const productionElementsOnSameYAxis = currentDaysProductionElements.filter((data) => data.y === newElement.y);
      const dataOnSameYAxis = [...productionElementsOnSameYAxis, ...scheduleElementsOnSameYAxis];
      const currentScheduleDayStart = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const currentScheduleDayEnd = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      let shouldCreateNewElement = true;
      let newElementStart = newElement.start;
      let newElementEnd = newElement.end;

      if (!newElement.wholeDayElement) {
        dataOnSameYAxis.forEach((existingElement) => {
          const newElementDuration = newElementEnd - newElementStart;
          if (newElementStart >= existingElement.start && newElementEnd <= existingElement.end) {
            shouldCreateNewElement = false;
          }

          // new element partially overlays existing element from the RIGHT side
          if (newElementStart < existingElement.end && newElementEnd > existingElement.end) {
            // finding available position for new element
            let isEmptySpace = true;
            // @ts-ignore
            dataOnSameYAxis.forEach((elementSameAxis) => {
              if (
                elementSameAxis !== existingElement &&
                elementSameAxis.start > existingElement.end &&
                existingElement.end + newElementDuration > elementSameAxis.start
              ) {
                if (existingElement.end === elementSameAxis.end) {
                  shouldCreateNewElement = false;
                } else {
                  newElementStart = existingElement.end;
                  newElementEnd = elementSameAxis.start;
                }
                isEmptySpace = false;
                return false;
              }
            });
            if (isEmptySpace) {
              // there IS an empty space for placing the new element
              newElementStart = existingElement.end;
              newElementEnd = existingElement.end + newElementDuration;
            }
            if (!isSameDay(newElementEnd, newElementStart)) {
              newElementEnd = startOfDay(newElementEnd).getTime();
            }
          }

          // new element partially overlays existing element from the LEFT side
          if (newElementStart < existingElement.start && newElementEnd > existingElement.start) {
            // finding available position for new element
            let isEmptySpace = true;
            dataOnSameYAxis.forEach((elementSameAxis) => {
              if (
                elementSameAxis !== existingElement &&
                elementSameAxis.start < existingElement.start &&
                existingElement.start - newElementDuration < elementSameAxis.end
              ) {
                if (existingElement.end === elementSameAxis.end) {
                  shouldCreateNewElement = false;
                } else {
                  newElementStart = elementSameAxis.end;
                  newElementEnd = existingElement.start;
                }
                isEmptySpace = false;
                return false;
              }
            });
            if (isEmptySpace) {
              // there IS an empty space for placing the new element
              newElementStart = maxDate([
                currentScheduleDayStart,
                existingElement.start - newElementDuration,
              ]).getTime();
              newElementEnd = existingElement.start;
            }
          }
        });
        // 24 Hrs elements like Vacation, Holiday... Must be calc separately as duration is unknown
      } else {
        /**
         * whole day element must not overflow the schedule range (extended by +1 extra day), in that case,
         * it should be shifted to the start/end of schedule range as a whole
         */
        const extendedScheduleEndTime = createEndOfDateRange(state.scheduleEndTime).getTime();
        const elementMinStart = Math.max(currentScheduleDayStart, state.scheduleStartTime);
        const elementMaxEnd = Math.min(currentScheduleDayEnd, extendedScheduleEndTime);

        // If no element is scheduled for agent
        if (isEmpty(dataOnSameYAxis)) {
          if (elementMinStart === state.scheduleStartTime) {
            // element should start at the start of schedule range
            newElementStart = state.scheduleStartTime;
            newElementEnd = createEndOfDateRange(newElementStart).getTime();

            const nearestElementNextDay = state.nearestSurroundingDaysElements.nextDay[newElement.agentId];
            if (nearestElementNextDay && newElementEnd > nearestElementNextDay.start) {
              // new element cannot overlap with nearest existing element in next day
              newElementEnd = nearestElementNextDay.start;
            }
          } else if (elementMaxEnd === extendedScheduleEndTime) {
            // element should end at the end of extended schedule range by +1 day
            newElementEnd = extendedScheduleEndTime;
            newElementStart = convertFieldsEndOfDateRange(newElementEnd).getTime();

            const nearestElementPreviousDay = state.nearestSurroundingDaysElements.previousDay[newElement.agentId];
            if (nearestElementPreviousDay && newElementStart < nearestElementPreviousDay.end) {
              // new element cannot overlap with nearest existing element in previous day
              newElementStart = nearestElementPreviousDay.end;
            }
          } else {
            // Element is added to whole day. Start is start of day and end is end of day
            newElementStart = currentScheduleDayStart;
            newElementEnd = currentScheduleDayEnd;
          }
        } else {
          // Only one element already scheduled
          if (dataOnSameYAxis.length === 1) {
            const existingElement = dataOnSameYAxis[0];

            if (existingElement.start >= newElementStart) {
              newElementStart = elementMinStart;
              newElementEnd = existingElement.start;
            } else {
              newElementStart = existingElement.end;
              newElementEnd = elementMaxEnd;
            }
          } else {
            // More then 1 element is scheduled
            const sortByStart = sortBy(prop('start'));
            const sameYAxisDataSorted = sortByStart(dataOnSameYAxis);

            const firstExistingElement = sameYAxisDataSorted[0];
            const lastExistingElement = last(sameYAxisDataSorted)!;

            const firstElementHalfDuration = Math.abs(firstExistingElement.end - firstExistingElement.start) / 2;

            // Left Edge case, start before the first scheduled element
            if (firstExistingElement.end - firstElementHalfDuration >= newElementStart) {
              newElementStart = elementMinStart;
              newElementEnd = firstExistingElement.start;
            } else if (lastExistingElement.start <= newElementStart) {
              // Right Edge case, start after the last scheduled element
              newElementStart = lastExistingElement.end;
              newElementEnd = elementMaxEnd;
            } else {
              // Search for the closes element in free gap to calculate start and end positions
              const distancesStartToNewStart: number[] = [];
              const distancesEndToNewStart: number[] = [];

              sameYAxisDataSorted.forEach((existingElement) => {
                distancesStartToNewStart.push(Math.abs(newElementStart - existingElement.start));
                distancesEndToNewStart.push(Math.abs(newElementStart - existingElement.end));
              });

              const minDistanceToStart = Math.min(...distancesStartToNewStart);
              const minDistanceToEnd = Math.min(...distancesEndToNewStart);

              if (minDistanceToStart <= minDistanceToEnd) {
                // Start of existing element is closer to drop point of new element
                const indexToClosesElementStart = distancesStartToNewStart.findIndex(
                  (distance) => distance === minDistanceToStart
                );

                // Elements from Left and Right side
                const closesElementToStart = sameYAxisDataSorted[indexToClosesElementStart]; // Right
                const closesToEndElement = sameYAxisDataSorted[indexToClosesElementStart - 1]; // Left

                // Setting final positions reversed
                newElementStart = closesToEndElement.end;
                newElementEnd = closesElementToStart.start;
              } else {
                // End of existing element is closer to drop point of new element
                const indexToClosesElementEnd = distancesEndToNewStart.findIndex(
                  (distance) => distance === minDistanceToEnd
                );

                // Elements from Left and Right side
                const closesElementToStart = sameYAxisDataSorted[indexToClosesElementEnd]; // Right
                const closesToEndElement = sameYAxisDataSorted[indexToClosesElementEnd + 1]; // Left

                // Setting final positions reversed
                newElementStart = closesElementToStart.end;
                newElementEnd = closesToEndElement.start;
              }
            }
          }
        }
      }

      if (newElementEnd - newElementStart < MIN_5) {
        // do not create elements with less duration than minimal 5 minutes duration!
        shouldCreateNewElement = false;
      }

      // cancel creation of the new element
      if (!shouldCreateNewElement)
        return {
          ...state,
          selectedScheduleElementsIds: [],
        };

      const newElementWithUpdatedTimestamp: ChartScheduleElement = {
        ...newElement,
        start: newElementStart,
        end: newElementEnd,
        dayIndexes: newElement.shift
          ? getDayIndexesForElement(newElementStart, newElementEnd, state.timezone, state.daysInScheduleTimezone)
          : [],
      };

      // new elements need to be added into the end of list so the work contribution could be calculated properly
      const updatedElements = [...state.usedScheduleElements, newElementWithUpdatedTimestamp];
      // all already existing elements should be unselected
      const updatedCurrentDaysElements = [...currentDaysElements.map(unselectElement), newElementWithUpdatedTimestamp];
      const updatedGanttData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: updatedCurrentDaysElements,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        }),
        usedScheduleElements: updatedElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: updatedGanttData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
        selectedScheduleElementsIds: [],
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.ADD_ELEMENT,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.REMOVE_ELEMENTS: {
      const removeIdsRecord = recordFromIds(state.selectedScheduleElementsIds, true);
      const usedElements = state.usedScheduleElements.filter(({ id }) => !removeIdsRecord[id]);

      const updatedCurrentDaysElements = getScheduleElementsForDateRange({
        startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min ?? 0,
        endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max ?? 0,
        scheduleElements: getAgentElementsWithCorrectPosition(usedElements, state.visibleSupervisedAgentIds),
      }).map(unselectElement);

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: updatedCurrentDaysElements,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
        }),
        usedScheduleElements: usedElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        selectedScheduleElementsIds: [],
        pinnedScheduleElementsIds: state.pinnedScheduleElementsIds.filter((id) => !removeIdsRecord[id]),
        pinnedUsedScheduleElementsIds: state.pinnedUsedScheduleElementsIds.filter((id) => !removeIdsRecord[id]),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.REMOVE_ELEMENTS,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.SELECT_AGENTS: {
      const selectedAgents = action.payload;
      const {
        agents,
        filter: { sortByFields, sortOrder },
      } = state;
      const allSelectedAgents = [...state.selectedAgentIds, ...selectedAgents];
      const allSelectedAgentsSorted = customSort(agents, sortByFields, sortOrder, allSelectedAgents);

      const scheduleStart = (state.chartState.xAxis as XAxisOptions[])[0].min!;
      const scheduleEnd = (state.chartState.xAxis as XAxisOptions[])[0].max!;

      const usedScheduleElements = state.usedScheduleElements.map(unselectElement);

      const nextDaySettings = Object.values(state.daysSettings).reduce<SchedulingState['daysSettings']>(
        (reduced, daySettings) => ({
          ...reduced,
          [daySettings.dayIndex]: {
            ...daySettings,
            agents: {
              ...daySettings.agents,
              ...selectedAgents.reduce(
                (reducedAgents, agentId) => ({
                  ...reducedAgents,
                  [agentId]: {
                    ...defaultAgentDaySettings,
                    agentId,
                  },
                }),
                {}
              ),
            },
          },
        }),
        {}
      );

      const sortedSupervisedAgentIds = filterSupervisedAgentIds(
        allSelectedAgentsSorted,
        state.supervisedAgentsRecordObj,
        state.notAssignedAgentIdsWithScheduleElement
      );

      const currentDaysElements = getScheduleElementsForDateRange({
        startTimestamp: scheduleStart,
        endTimestamp: scheduleEnd,
        scheduleElements: getAgentElementsWithCorrectPosition(usedScheduleElements, sortedSupervisedAgentIds),
      });

      const ganttElementsData = currentDaysElements.filter(({ agentId }) => state.visibleAgentIdsInViewPort[agentId]);

      const newState: SchedulingState = {
        ...state,
        usedScheduleElements,
        isDirty: true,
        selectedAgentIds: allSelectedAgents,
        selectedAgentIdsSorted: allSelectedAgentsSorted,
        /**
         * this should also happen in followed-up action SHOW_ALL_AGENTS,
         * however, didn't work well for situation when adding new agents to empty scene
         * and so chart in some cases wasn't rendered correctly
         */
        visibleAgentIds: allSelectedAgents,
        visibleSupervisedAgentIds: sortedSupervisedAgentIds,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            height: computeChartsHeight(sortedSupervisedAgentIds),
          },
          plotOptions: {
            ...state.chartState.plotOptions,
            series: {
              ...state.chartState.plotOptions!.series,
              dragDrop: {
                ...state.chartState.plotOptions!.series!.dragDrop,
                dragMaxY: Math.max(0, sortedSupervisedAgentIds.length - 1),
              },
            },
          },
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              max: Math.max(0, sortedSupervisedAgentIds.length - 1),
              gridLineWidth: computeYAxisLineWidth(sortedSupervisedAgentIds),
            },
          ],
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                sortedSupervisedAgentIds,
                scheduleStart,
                scheduleEnd
              ),
            },
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        daysSettings: nextDaySettings,
        nearestSurroundingDaysElements: findNearestElementsWithinSurroundingDays(
          state.usedScheduleElements,
          scheduleStart,
          scheduleEnd
        ),
        selectedScheduleElementsIds: [],
        usedScheduleElementsWithinCurrentDay: currentDaysElements,
        schedulingAction: SchedulingAction.SELECT_AGENTS,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.REMOVE_AGENTS: {
      const { removedAgentsIds } = action.payload;

      if (!state.selectedAgentIds.length) {
        return state;
      }
      const filteredAgents = state.selectedAgentIds.filter((agentId) =>
        removedAgentsIds.every((removedAgentId) => removedAgentId !== agentId)
      );

      const filteredSortedAgents = state.selectedAgentIdsSorted.filter((agentId) =>
        removedAgentsIds.every((removedAgentId) => removedAgentId !== agentId)
      );

      const filteredInactiveAgents = state.inactiveAgentIdsWithScheduleElement.filter((agentId) =>
        removedAgentsIds.every((removedAgentId) => removedAgentId !== agentId)
      );

      const filteredNotAssignedAgentIds = state.notAssignedAgentIdsWithScheduleElement.filter((agentId) =>
        removedAgentsIds.every((removedAgentId) => removedAgentId !== agentId)
      );

      const updatedUsedScheduleElements = state.usedScheduleElements
        .filter((element) => removedAgentsIds.every((removedAgentId) => removedAgentId !== element.agentId))
        .map(unselectElement);

      const sortedSupervisedAgentIds = filterSupervisedAgentIds(
        filteredSortedAgents,
        state.supervisedAgentsRecordObj,
        state.notAssignedAgentIdsWithScheduleElement
      );

      const updatedScheduleElements = getAgentElementsWithCorrectPosition(
        updatedUsedScheduleElements,
        sortedSupervisedAgentIds
      );

      const updatedCurrentDaysElements = getScheduleElementsForDateRange({
        startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min ?? 0,
        endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max ?? 0,
        scheduleElements: getAgentElementsWithCorrectPosition(updatedUsedScheduleElements, sortedSupervisedAgentIds),
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const nextDaySettings = Object.values(state.daysSettings).reduce<SchedulingState['daysSettings']>(
        (reduced, daySettings) => ({
          ...reduced,
          [daySettings.dayIndex]: {
            ...daySettings,
            agents: filteredAgents.reduce(
              (reducedAgents, agentId) => ({
                ...reducedAgents,
                [agentId]: {
                  ...state.daysSettings[state.pickedDayIndex].agents[agentId],
                },
              }),
              {}
            ),
          },
        }),
        {}
      );

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        inactiveAgentIdsWithScheduleElement: filteredInactiveAgents,
        notAssignedAgentIdsWithScheduleElement: filteredNotAssignedAgentIds,
        selectedAgentIds: filteredAgents,
        selectedAgentIdsSorted: filteredSortedAgents,
        schedulingChartState:
          state.queueIds.length === 1
            ? // precalculated queues staffing cannot be applied, need to recalculate contributions manually
              mapChartData({
                agents: state.agents,
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                schedulingChartType: state.schedulingChartType,
                productiveElementIds: state.productiveElementsIds,
                queueIds: state.queueIds,
                queues: state.queues,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                scheduleElements: updatedCurrentDaysElements,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
                workContributionData: state.workContributionData,
                productionElementsWorkContribution: state.productionElementsWorkContributionData,
                productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
                productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
              })
            : state.schedulingChartState,
        usedScheduleElements: updatedScheduleElements,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            height: computeChartsHeight(sortedSupervisedAgentIds),
          },
          plotOptions: {
            ...state.chartState.plotOptions,
            series: {
              // @ts-ignore
              ...state.chartState.plotOptions.series,
              dragDrop: {
                ...state.chartState.plotOptions!.series!.dragDrop,
                dragMaxY: sortedSupervisedAgentIds.length - 1,
              },
            },
          },
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              max: sortedSupervisedAgentIds.length - 1,
              gridLineWidth: computeYAxisLineWidth(sortedSupervisedAgentIds),
            },
          ],
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            /**
             * needs to return previous state as it was causing inconsistency in chart's rendering,
             * this value will be changed along with SHOW_ALL_AGENTS anyway
             */
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        daysSettings: nextDaySettings,
        pinnedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(updatedScheduleElements, 'pinned'),
        selectedScheduleElementsIds: [],
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.REMOVE_AGENTS,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.UPDATE_ELEMENTS: {
      const { updatedPoints } = action;
      const newChartSeriesDataRecord = recordBy(state.usedScheduleElementsWithinCurrentDay, 'key');
      const newPointsKeysRecord = recordBy(updatedPoints, 'key');
      const usedElementsWithoutUpdated = state.usedScheduleElements.filter(({ key }) => !newPointsKeysRecord[key]);
      const newUsedElements: ChartScheduleElement[] = [];

      const scheduleStart = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const scheduleEnd = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      let shouldDoUpdateOfNearestDaysElements = false;

      updatedPoints.forEach((point) => {
        const newDataChunk = {
          ...newChartSeriesDataRecord[point.key],
          start: point.start,
          end: point.end,
          agentId: state.visibleSupervisedAgentIds[point.y!],
          y: point.y,
          dayIndexes: point.shift
            ? getDayIndexesForElement(point.start, point.end, state.timezone, state.daysInScheduleTimezone)
            : [],
        };

        if (point.start >= scheduleEnd || point.end <= scheduleStart) {
          shouldDoUpdateOfNearestDaysElements = true;
        }

        newChartSeriesDataRecord[point.key] = newDataChunk;
        newUsedElements.push(newDataChunk);
      });

      /**
       * Preserve element order between actions
       */
      const newUsedElementsWithinCurrentDay: ChartScheduleElement[] = Object.values(newChartSeriesDataRecord);

      const ganttElementsData = newUsedElementsWithinCurrentDay.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const newUsedScheduleElements = [...usedElementsWithoutUpdated, ...newUsedElements];

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: scheduleEnd,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: newUsedElementsWithinCurrentDay,
          startTimestamp: scheduleStart,
          workContributionData: state.workContributionData,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
        }),
        usedScheduleElements: newUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        nearestSurroundingDaysElements: shouldDoUpdateOfNearestDaysElements
          ? findNearestElementsWithinSurroundingDays(newUsedScheduleElements, scheduleStart, scheduleEnd)
          : state.nearestSurroundingDaysElements,
        pinnedScheduleElementsIds: getElementsKeysWithFlag(newUsedElementsWithinCurrentDay, 'pinned'),
        usedScheduleElementsWithinCurrentDay: newUsedElementsWithinCurrentDay,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.UPDATE_ELEMENTS,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.COPY_SCHEDULE: {
      const { agentId } = action.payload;
      const scheduleGanttElementsForCurrentDay = state.usedScheduleElementsWithinCurrentDay;

      // start/end range for current Date
      const min = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const max = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      // Find all elements for copy based on agentID and cut off possible overflow to surrounding days
      const copiedScheduleElements = scheduleGanttElementsForCurrentDay
        .filter((element) => element.agentId === agentId)
        .map(cropElementsOuterRangeOverflow(min, max, state.timezone, state.daysInScheduleTimezone));

      let dayIndex = state.pickedDayIndex;

      // In case of +1 extra day out of schedule range, previous day settings should be modified
      if (dayIndex === Object.keys(state.daysSettings).length) {
        dayIndex -= 1;
      }

      const { agentId: deletedAgentId, ...copiedAgentDaySettings } = state.daysSettings[dayIndex].agents[agentId];

      return {
        ...state,
        clipboard: {
          copiedScheduleElements,
          isPasteScheduleActive: true,
          agentDaySettings: copiedAgentDaySettings,
        },
        schedulingAction: SchedulingAction.COPY_SCHEDULE,
      };
    }

    case SchedulingAction.PASTE_SCHEDULE: {
      const { agentId, y } = action.payload;
      const usedScheduleGanttElementsForCurrentDay = state.usedScheduleElementsWithinCurrentDay;

      // start/end range for current Date
      const min = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const max = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      // start/end schedule range
      const scheduleMin = state.scheduleStartTime;
      const scheduleMax = addDays(state.scheduleEndTime, 1).getTime(); // elements could be pasted into +1 extra day as well

      let dayIndex = state.pickedDayIndex;

      // In case of +1 extra day out of schedule range, previous day settings should be modified
      if (dayIndex === Object.keys(state.daysSettings).length) {
        dayIndex -= 1;
      }

      const productionElementsPerAgent = {} as Record<string, ChartScheduleElement[]>;

      state.productionScheduleElementsWithinCurrentDay.forEach((element) => {
        if (!productionElementsPerAgent[element.agentId]) {
          productionElementsPerAgent[element.agentId] = [];
        }

        productionElementsPerAgent[element.agentId].push(element);
      });

      // Setting new key, target Y index, agentId and proper date
      const elementsToPaste = (
        state.clipboard.copiedScheduleElements
          .map((element) =>
            createElementToPaste(
              min,
              max,
              scheduleMin,
              scheduleMax,
              element,
              agentId,
              true,
              state.viewedTimezone,
              state.timezone, // schedule time zone
              state.daysInScheduleTimezone,
              y
            )
          )
          .filter((element) => element) as ChartScheduleElement[]
      ).filter(
        // Filter out elements being in collision with any production element
        (element) =>
          !productionElementsPerAgent[element.agentId]?.find(({ start, end }) => elementInRange(start, end, element))
      );

      // Deleting previous gantt items on targeted agent
      const seriesGanttFilteredForTarget = usedScheduleGanttElementsForCurrentDay
        .filter((element) => {
          return element.agentId !== agentId;
        })
        .map(unselectElement);

      // Deleting scheduled items on targeted agent for current Date range
      const usedScheduledElementFilteredForTarget = state.usedScheduleElements
        .filter(
          (element) =>
            element.agentId !== agentId ||
            (!isWithinInterval(element.start, { start: min, end: max - 1 }) &&
              !isWithinInterval(element.end, { start: min + 1, end: max }))
        )
        .map(unselectElement);

      // Cropping existing scheduled items that are exceeding into current Date range
      const croppedExceedingUsedScheduleElements = state.usedScheduleElements
        .filter(
          (element) =>
            element.agentId === agentId &&
            // element exceeds from current day into following one
            ((isWithinInterval(element.start, { start: min, end: max - 1 }) &&
              !isWithinInterval(element.end, { start: min + 1, end: max })) ||
              // element exceeds from previous day into current one
              (isWithinInterval(element.end, { start: min + 1, end: max }) &&
                !isWithinInterval(element.start, { start: min, end: max - 1 })))
        )
        .map(cropElementsInnerRangeOverlap(min, max, state.timezone, state.daysInScheduleTimezone))
        .map(unselectElement);

      // Creating final data objects, selected flag active only in current day gantt data
      const updatedElementsNew = [
        ...elementsToPaste,
        ...croppedExceedingUsedScheduleElements,
        ...usedScheduledElementFilteredForTarget,
      ];

      const updatedCurrentDaysElements = [...elementsToPaste, ...seriesGanttFilteredForTarget];

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const agentDaySettingsToPaste = state.clipboard.agentDaySettings;

      const newDaysSettings: Record<number, DaySettings> = {
        ...state.daysSettings,
        [dayIndex]: {
          ...state.daysSettings[dayIndex],
          agents: {
            ...state.daysSettings[dayIndex].agents,
            [agentId]: {
              ...state.daysSettings[dayIndex].agents[agentId],
              ...agentDaySettingsToPaste,
            },
          },
        },
      };

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: updatedCurrentDaysElements,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        }),
        usedScheduleElements: updatedElementsNew,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        daysSettings: newDaysSettings,
        pinnedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(updatedElementsNew, 'pinned'),
        selectedScheduleElementsIds: elementsToPaste.map(({ key }) => key),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.PASTE_SCHEDULE,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    /**
     * CLONE_SCHEDULE could be applied in two ways
     * `all agents` or `specific agent(s)` schedule clone differentiate based on argument for `agentIds`
     * if `agentIds` is/are provided then `specific agent(s)` schedule clone will be executed otherwise
     * if `agentIds` is empty array then `all agents` schedule clone will be executed
     */
    case SchedulingAction.CLONE_SCHEDULE: {
      const { agentIds, dates } = action.payload;
      const allAgentsCloneType = agentIds.length === 0;
      const scheduleGanttElementsForCurrentDay = state.usedScheduleElementsWithinCurrentDay;

      // Initial timeLine start shift due to TimeZone difference
      const currentDateStart = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const currentDateEnd = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      // start/end schedule range
      const scheduleMin = state.scheduleStartTime;
      const scheduleMax = addDays(state.scheduleEndTime, 1).getTime(); // elements could be cloned into +1 extra day as well

      let dayIndex = state.pickedDayIndex;

      // In case of +1 extra day out of schedule range, previous day settings should be modified
      const extraDayIndex = Object.keys(state.daysSettings).length;
      if (dayIndex === extraDayIndex) {
        dayIndex -= 1;
      }

      /**
       * Find all elements for clone optionally based on agentId in current Date
       * and cut off possible overflow to surrounding days
       */
      const copiedScheduleElements = getScheduleElementsForDateRange({
        startTimestamp: zonedTimeToUtc(currentDateStart, state.viewedTimezone).getTime(),
        endTimestamp: zonedTimeToUtc(currentDateEnd, state.viewedTimezone).getTime(),
        scheduleElements: allAgentsCloneType
          ? scheduleGanttElementsForCurrentDay
          : scheduleGanttElementsForCurrentDay.filter(({ agentId }) => agentIds.includes(agentId)),
      }).map(
        cropElementsOuterRangeOverflow(currentDateStart, currentDateEnd, state.timezone, state.daysInScheduleTimezone)
      );

      const productionElementsPerAgent = {} as Record<string, ChartScheduleElement[]>;

      state.productionScheduleElements.forEach((element) => {
        if (!productionElementsPerAgent[element.agentId]) {
          productionElementsPerAgent[element.agentId] = [];
        }

        productionElementsPerAgent[element.agentId].push(element);
      });

      // Cloning copied elements to chosen dates
      const clonedElements = dates.flatMap((date) =>
        (
          copiedScheduleElements
            .map((element) =>
              createElementToPaste(
                zonedTimeToUtc(date, state.viewedTimezone).getTime(),
                zonedTimeToUtc(addDays(date, 1), state.viewedTimezone).getTime(),
                scheduleMin,
                scheduleMax,
                element,
                undefined,
                false,
                state.viewedTimezone,
                state.timezone, // schedule time zone
                state.daysInScheduleTimezone,
                undefined
              )
            )
            .filter((element) => element) as ChartScheduleElement[]
        ).filter(
          // Filter out elements being in collision with any production element
          (element) =>
            !productionElementsPerAgent[element.agentId]?.find(({ start, end }) => elementInRange(start, end, element))
        )
      );

      // Find all elements to delete in given date range
      const elementsOfChosenAgent = allAgentsCloneType
        ? state.usedScheduleElements
        : state.usedScheduleElements.filter(({ agentId }) => agentIds.includes(agentId));

      const elementsToDelete = dates.flatMap((date) =>
        getScheduleElementsForDateRange({
          startTimestamp: zonedTimeToUtc(date, state.viewedTimezone).getTime(),
          endTimestamp: zonedTimeToUtc(createEndOfDateRange(date), state.viewedTimezone).getTime(),
          scheduleElements: elementsOfChosenAgent,
        })
      );

      // find continuous ranges within selected dates
      const chosenBlockIntervals =
        dates.length === 1
          ? [
              {
                start: dates[0],
                end: createEndOfDateRange(dates[0]),
              },
            ]
          : dates.reduce<Array<{ start: Date; end: Date }>>((reducedDates, date) => {
              if (reducedDates.length === 0 || reducedDates[reducedDates.length - 1].end.getTime() !== date.getTime()) {
                reducedDates.push({
                  start: date,
                  end: createEndOfDateRange(date),
                });
              } else {
                reducedDates = [
                  ...reducedDates.slice(0, reducedDates.length - 1),
                  {
                    ...reducedDates[reducedDates.length - 1],
                    end: createEndOfDateRange(date),
                  },
                ];
              }

              return reducedDates;
            }, []);

      const elementsToCrop = chosenBlockIntervals.flatMap((range) => {
        const startDate = zonedTimeToUtc(range.start, state.viewedTimezone).getTime();
        const endDate = zonedTimeToUtc(range.end, state.viewedTimezone).getTime();

        return elementsOfChosenAgent
          .filter(
            (element) =>
              // element exceeds from current day into following one
              (isWithinInterval(element.start, { start: startDate, end: endDate - 1 }) &&
                !isWithinInterval(element.end, { start: startDate + 1, end: endDate })) ||
              // element exceeds from previous day into current one
              (isWithinInterval(element.end, { start: startDate + 1, end: endDate }) &&
                !isWithinInterval(element.start, { start: startDate, end: endDate - 1 }))
          )
          .map(cropElementsInnerRangeOverlap(startDate, endDate, state.timezone, state.daysInScheduleTimezone));
      });

      const elementsToCropInCurrentDay = elementsToCrop.filter(
        (element) =>
          // pick those elements whose start or end time is within range of current day
          isWithinInterval(element.start, { start: currentDateStart, end: currentDateEnd - 1 }) ||
          isWithinInterval(element.end, { start: currentDateStart + 1, end: currentDateEnd })
      );

      // Create object for all usedScheduleElements without one to delete
      const elementsToDeleteRecord = recordBy(elementsToDelete, 'key');
      const usedScheduleElementsFiltered = state.usedScheduleElements.filter(
        (element) => !elementsToDeleteRecord[element.key]
      );

      const clonedAgentsDaySettings = (allAgentsCloneType ? state.selectedAgentIds : agentIds).reduce(
        (reduced, agentId) => ({
          ...reduced,
          [agentId]: state.daysSettings[dayIndex].agents[agentId],
        }),
        {}
      );

      const dayToCloneIndices = dates.map((date) => closestIndexTo(date, state.days) ?? 0);
      const clonedDaysSettings = dayToCloneIndices.reduce((reduced, currentDayIndex) => {
        let index = currentDayIndex;

        if (currentDayIndex === extraDayIndex) {
          // extra day doesn't contain own daySettings, it only shows settings from previous day
          index -= 1;
        }

        // skip adding settings when new value was already prepared or it would modify current day
        if (reduced[index] || index === state.pickedDayIndex) {
          return reduced;
        }

        return {
          ...reduced,
          [index]: {
            ...state.daysSettings[index],
            agents: {
              ...state.daysSettings[index].agents,
              ...clonedAgentsDaySettings,
            },
          },
        };
      }, {} as Record<number, DaySettings>);

      const nextDaysSettings: Record<number, DaySettings> = {
        ...state.daysSettings,
        ...clonedDaysSettings,
      };

      const updatedUsedScheduleElements = [...usedScheduleElementsFiltered, ...elementsToCrop, ...clonedElements];

      const updatedCurrentDaysElements = elementsToCropInCurrentDay.length
        ? [
            ...differenceWith(
              (x: ChartScheduleElement, y: ChartScheduleElement) => x.key === y.key,
              state.usedScheduleElementsWithinCurrentDay,
              elementsToCropInCurrentDay
            ),
            ...elementsToCropInCurrentDay,
          ]
        : [];

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        daysSettings: nextDaysSettings,
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(updatedUsedScheduleElements, 'pinned'),
        schedulingAction: SchedulingAction.CLONE_SCHEDULE,
        ...(elementsToCropInCurrentDay.length
          ? {
              chartState: {
                ...state.chartState,
                series: [
                  {
                    ...(state.chartState.series as SeriesOptionsType[])[0],
                    data: ganttElementsData,
                  },
                  (state.chartState.series as SeriesOptionsType[])[1],
                  (state.chartState.series as SeriesOptionsType[])[2],
                ] as SeriesOptionsType[],
              },
              pinnedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
              usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
            }
          : {}),
      };

      return newStateWithHistory(state.scheduleHistory, newState, { type: 'clone', snackBarMessage: true });
    }

    case SchedulingAction.CLEAR_SCHEDULE: {
      const { agentId } = action.payload;
      const scheduleGanttElementsForCurrentDay = state.usedScheduleElementsWithinCurrentDay;

      const cleanedScheduleElementsForCurrentDay = scheduleGanttElementsForCurrentDay.filter(
        (element) => element.agentId !== agentId
      );

      const ganttElementsData = cleanedScheduleElementsForCurrentDay.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      // start/end range for current Date
      const min = (state.chartState.xAxis as XAxisOptions[])[0].min as number;
      const max = (state.chartState.xAxis as XAxisOptions[])[0].max as number;

      // Deleting all scheduled items on targeted agent for current Date range
      const cleanedScheduleElementsAll = state.usedScheduleElements.filter(
        (element) =>
          element.agentId !== agentId ||
          (!isWithinInterval(element.start, { start: min, end: max - 1 }) &&
            !isWithinInterval(element.end, { start: min + 1, end: max }))
      );

      const newState: SchedulingState = {
        ...state,
        isDirty: true,
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: cleanedScheduleElementsForCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        }),
        usedScheduleElements: cleanedScheduleElementsAll,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(cleanedScheduleElementsForCurrentDay, 'pinned'),
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(cleanedScheduleElementsAll, 'pinned'),
        selectedScheduleElementsIds: getElementsKeysWithFlag(cleanedScheduleElementsForCurrentDay, 'selected'),
        usedScheduleElementsWithinCurrentDay: cleanedScheduleElementsForCurrentDay,
        usePrecalculatedQueuesStaffing: false,
        schedulingAction: SchedulingAction.CLEAR_SCHEDULE,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.SELECT_ALL_AGENT_SCHEDULE: {
      const { agentId } = action.payload;
      const scheduleGanttElementsForCurrentDay = state.usedScheduleElementsWithinCurrentDay;

      const selectedScheduleElementsIds: string[] = [];

      const scheduleElementsForCurrentDaySelectedForAgent = scheduleGanttElementsForCurrentDay.map((element) => {
        if (element.agentId === agentId) {
          selectedScheduleElementsIds.push(element.key);

          return {
            ...element,
            selected: true,
          };
        }

        return {
          ...element,
          selected: false,
        };
      });

      const ganttElementsData = scheduleElementsForCurrentDaySelectedForAgent.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        selectedScheduleElementsIds,
        // contribution shouldn't be affected by this action, no need to recalculate it
        schedulingChartState: state.schedulingChartState,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        usedScheduleElementsWithinCurrentDay: scheduleElementsForCurrentDaySelectedForAgent,
        schedulingAction: SchedulingAction.SELECT_ALL_AGENT_SCHEDULE,
      };
    }

    case SchedulingAction.UPDATE_DAY_SETTING: {
      const { setting, value } = action.payload;
      let dayIndex = action.payload.dayIndex;

      // In case of +1 extra day out of schedule range, previous day settings should be modified
      if (dayIndex === Object.keys(state.daysSettings).length) {
        dayIndex -= 1;
      }

      const nextDaySettings = {
        ...(state.daysSettings[dayIndex] || defaultDaySettings),
        [setting]: value,
      };

      return {
        ...state,
        daysSettings: {
          ...state.daysSettings,
          [dayIndex]: nextDaySettings,
        } as Record<number, DaySettings>,
      };
    }

    case SchedulingAction.UPDATE_AGENT_DAY_SETTING: {
      const { agentId, dayIndex, setting, value } = action.payload;

      const nextAgentDaySettings = {
        ...(state.daysSettings[dayIndex].agents[agentId] || defaultAgentDaySettings),
        [setting]: value,
      };

      const newState = {
        ...state,
        isDirty: true,
        daysSettings: {
          ...state.daysSettings,
          [dayIndex]: {
            ...state.daysSettings[dayIndex],
            agents: {
              ...state.daysSettings[dayIndex].agents,
              [agentId]: nextAgentDaySettings,
            },
          },
        } as Record<number, DaySettings>,
      };

      return newStateWithHistory(state.scheduleHistory, newState);
    }

    case SchedulingAction.HIDE_NON_WORKING_AGENTS: {
      const {
        chartState,
        selectedAgentIdsSorted,
        usedScheduleElementsWithinCurrentDay,
        productionScheduleElementsWithinCurrentDay,
      } = state;

      const visibleAgentIds = getWorkingAgents(selectedAgentIdsSorted, usedScheduleElementsWithinCurrentDay);

      const startDate = (chartState.xAxis as XAxisOptions[])[0].min!;
      const endDate = (chartState.xAxis as XAxisOptions[])[0].max!;
      const visibleSupervisedAgentIds = filterSupervisedAgentIds(
        visibleAgentIds,
        state.supervisedAgentsRecordObj,
        state.notAssignedAgentIdsWithScheduleElement
      );

      const usedCurrentDaysElementsUpdated = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          usedScheduleElementsWithinCurrentDay,
          visibleSupervisedAgentIds
        ),
      });

      const ganttElementsData = usedCurrentDaysElementsUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const productionScheduleElementsWithinCurrentDayUpdated = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          productionScheduleElementsWithinCurrentDay,
          visibleSupervisedAgentIds
        ),
      });

      const productionScheduleElementsInGanttUpdated = productionScheduleElementsWithinCurrentDayUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const selectedScheduleElementsIds = getElementsKeysWithFlag(usedCurrentDaysElementsUpdated, 'selected');

      return {
        ...state,
        selectedScheduleElementsIds,
        visibleAgentIds,
        visibleSupervisedAgentIds,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            height: computeChartsHeight(visibleSupervisedAgentIds),
          },
          plotOptions: {
            ...state.chartState.plotOptions,
            series: {
              // @ts-ignore
              ...state.chartState.plotOptions.series,
              dragDrop: {
                ...state.chartState.plotOptions!.series!.dragDrop,
                dragMaxY: Math.max(0, visibleSupervisedAgentIds.length - 1),
              },
            },
          },
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              max: Math.max(0, visibleSupervisedAgentIds.length - 1),
              gridLineWidth: computeYAxisLineWidth(visibleSupervisedAgentIds),
            },
          ],
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                visibleSupervisedAgentIds,
                (state.chartState.xAxis as XAxisOptions[])[0].min!,
                (state.chartState.xAxis as XAxisOptions[])[0].max!
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionScheduleElementsInGanttUpdated),
            },
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(usedCurrentDaysElementsUpdated, 'pinned'),
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsWithinCurrentDayUpdated,
        usedScheduleElementsWithinCurrentDay: usedCurrentDaysElementsUpdated,
      };
    }

    case SchedulingAction.SHOW_ALL_AGENTS: {
      const {
        chartState,
        productionScheduleElements,
        selectedAgentIdsSorted,
        usedScheduleElements,
        usedScheduleElementsWithinCurrentDay,
      } = state;

      const selectedScheduleElementsIds: string[] = [];

      const startDate = (chartState.xAxis as XAxisOptions[])[0].min!;
      const endDate = (chartState.xAxis as XAxisOptions[])[0].max!;

      // Handle correct `selected` state for points
      const scheduleElements = usedScheduleElements.map((usedElement) => {
        const point = usedScheduleElementsWithinCurrentDay.find((element) => element.key === usedElement.key);
        const selected = point ? point.selected : usedElement.selected;

        if (selected) {
          selectedScheduleElementsIds.push(usedElement.key);
        }

        return {
          ...usedElement,
          selected,
        };
      });

      const visibleSupervisedAgentIds = filterSupervisedAgentIds(
        selectedAgentIdsSorted,
        state.supervisedAgentsRecordObj,
        state.notAssignedAgentIdsWithScheduleElement
      );

      const usedCurrentDaysElementsUpdated = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(scheduleElements, visibleSupervisedAgentIds),
      });

      const productionScheduleElementsWithinCurrentDayUpdated = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(productionScheduleElements, visibleSupervisedAgentIds),
      });

      const productionScheduleElementsInGanttUpdated = productionScheduleElementsWithinCurrentDayUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const ganttElementsData = usedCurrentDaysElementsUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        selectedScheduleElementsIds,
        visibleAgentIds: selectedAgentIdsSorted,
        visibleSupervisedAgentIds,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            height: computeChartsHeight(visibleSupervisedAgentIds),
          },
          plotOptions: {
            ...state.chartState.plotOptions,
            series: {
              // @ts-ignore
              ...state.chartState.plotOptions.series,
              dragDrop: {
                ...state.chartState.plotOptions!.series!.dragDrop,
                dragMaxY: Math.max(0, visibleSupervisedAgentIds.length - 1),
              },
            },
          },
          yAxis: [
            {
              ...(state.chartState.yAxis as YAxisOptions[])[0],
              max: Math.max(0, visibleSupervisedAgentIds.length - 1),
              gridLineWidth: computeYAxisLineWidth(visibleSupervisedAgentIds),
            },
          ],
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                visibleSupervisedAgentIds,
                (state.chartState.xAxis as XAxisOptions[])[0].min!,
                (state.chartState.xAxis as XAxisOptions[])[0].max!
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionScheduleElementsInGanttUpdated),
            },
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(usedCurrentDaysElementsUpdated, 'pinned'),
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsWithinCurrentDayUpdated,
        usedScheduleElementsWithinCurrentDay: usedCurrentDaysElementsUpdated,
      };
    }

    case SchedulingAction.REWIND_SCHEDULE: {
      const { statePosition } = action.payload;
      const { schedulingStateHistory } = state.scheduleHistory;

      if (statePosition < 0 || statePosition + 1 > schedulingStateHistory.length) {
        return state;
      }

      const newState = omit(['actionType'], schedulingStateHistory[statePosition]);

      const startDate = (newState.chartState.xAxis as XAxisOptions[])[0].min!;
      const endDate = (newState.chartState.xAxis as XAxisOptions[])[0].max!;

      const updatedCurrentDaysElements = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          newState.usedScheduleElements,
          newState.visibleSupervisedAgentIds
        ),
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const productionScheduleElementsUpdated = getScheduleElementsForDateRange({
        startTimestamp: startDate,
        endTimestamp: endDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          state.productionScheduleElements,
          newState.visibleSupervisedAgentIds
        ),
      });

      const productionScheduleElementsForGantt = productionScheduleElementsUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        ...newState,
        // in case of no change to schedule was provided by user in rewound state, pre-calculated contribution data should be used!
        schedulingChartState:
          state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production
            ? mapInitialChartData({
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                initialStaffingPerQueue: state.initialQueuesStaffingData,
                queueIds: state.queueIds,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
              })
            : mapChartData({
                agents: state.agents,
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                schedulingChartType: state.schedulingChartType,
                productionElementsWorkContribution: state.productionElementsWorkContributionData,
                productionScheduleElements: productionScheduleElementsUpdated,
                productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
                productiveElementIds: state.productiveElementsIds,
                queueIds: state.queueIds,
                queues: state.queues,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                scheduleElements: updatedCurrentDaysElements,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
                workContributionData: state.workContributionData,
              }),

        chartState: {
          ...state.chartState,
          ...newState.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                state.visibleSupervisedAgentIds,
                (state.chartState.xAxis as XAxisOptions[])[0].min!,
                (state.chartState.xAxis as XAxisOptions[])[0].max!
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionScheduleElementsForGantt),
            },
          ] as SeriesOptionsType[],
          plotOptions: {
            ...state.chartState.plotOptions,
            ...newState.chartState.plotOptions,
            series: {
              ...state.chartState.plotOptions!.series,
              ...newState.chartState.plotOptions!.series,
            },
          },
        },
        scheduleHistory: {
          schedulingStateHistory: [...schedulingStateHistory],
          positionInHistory: statePosition,
        },
        pinnedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'pinned'),
        pinnedUsedScheduleElementsIds: getElementsKeysWithFlag(newState.usedScheduleElements, 'pinned'),
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsUpdated,
        selectedScheduleElementsIds: getElementsKeysWithFlag(updatedCurrentDaysElements, 'selected'),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.REWIND_SCHEDULE,
      };
    }

    /**
     * Adjusting scheduleHistory to hold `isDirty:false` flag on the positionInHistory state and
     * resetting original `isDirty:false` flag to `true` on the first stored state after SAVING schedule action.
     */
    case SchedulingAction.RESET_DIRTY_FLAG: {
      const { positionInHistory, schedulingStateHistory } = state.scheduleHistory;

      const newSchedulingStateHistory = schedulingStateHistory.map((history, index) => {
        const isDirty = index !== positionInHistory;
        return { ...history, isDirty };
      });

      return {
        ...state,
        isDirty: false,
        scheduleHistory: {
          positionInHistory,
          schedulingStateHistory: newSchedulingStateHistory,
        },
        schedulingAction: SchedulingAction.RESET_DIRTY_FLAG,
      };
    }

    case SchedulingAction.LOAD_WORK_CONTRIBUTION: {
      return {
        ...state,
        isCalculatingWorkContributionData: true,
      };
    }

    case SchedulingAction.LOAD_WORK_CONTRIBUTION_FAILED: {
      return {
        ...state,
        isCalculatingWorkContributionData: false,
        // schedulingChartState state needs to be recalculated as forecast may have changed in meantime
        schedulingChartState: mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: state.usedScheduleElementsWithinCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: {},
        }),
        workContributionData: {},
      };
    }

    case SchedulingAction.CALCULATE_WORK_CONTRIBUTION: {
      const { workContributionInputData } = action.payload;

      const loadContributionData = () => {
        /**
         * In case of no change to schedule was provided by user, pre-calculated contribution data should be used!
         * This is not valid in case of intraday forecast is being used as source data, due to BE doesn't restore calculated
         * intraday queues staffing as these data are not useful for them yet - we need to calculate Work contribution manually.
         */
        if (state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production) {
          return mapInitialChartData({
            endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
            forecast: state.forecast,
            queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
            initialStaffingPerQueue: state.initialQueuesStaffingData,
            queueIds: state.queueIds,
            startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          });
        }

        return mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: state.usedScheduleElementsWithinCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: workContributionInputData,
        });
      };

      return {
        ...state,
        isCalculatingWorkContributionData: false,
        schedulingChartState:
          state.isLoadingForecast ||
          state.isLoadingProductionScheduleElements ||
          state.isLoadingQueuesStaffingOfProductionElements
            ? state.schedulingChartState
            : loadContributionData(),
        workContributionData: workContributionInputData,
      };
    }

    case SchedulingAction.LOAD_FORECAST_DATA: {
      return {
        ...state,
        isLoadingForecast: true,
      };
    }

    case SchedulingAction.LOAD_FORECAST_DATA_FAILED: {
      return {
        ...state,
        forecast: {},
        isLoadingForecast: false,
        missingForecastIntervalsPerQueue: {},
        missingOrIncompleteForecastQueueIds: [],
      };
    }

    case SchedulingAction.RESTORE_FORECAST_DATA: {
      const { forecast, missingForecastIntervalsPerQueue, missingOrIncompleteForecastQueueIds } = action.payload;

      const loadContributionData = () => {
        /**
         * In case of no change to schedule was provided by user, pre-calculated contribution data should be used!
         * This is not valid in case of intraday forecast is being used as source data, due to BE doesn't restore calculated
         * intraday queues staffing as these data are not useful for them yet - we need to calculate Work contribution manually.
         */
        if (state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production) {
          return mapInitialChartData({
            forecast,
            endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
            initialStaffingPerQueue: state.initialQueuesStaffingData,
            queueIds: state.queueIds,
            queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
            startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          });
        }

        return mapChartData({
          forecast,
          schedulingChartType: state.schedulingChartType,
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: state.usedScheduleElementsWithinCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        });
      };

      return {
        ...state,
        forecast,
        forecastDataType:
          state.schedulingChartType === SchedulingChartType.Intraday ? ForecastType.Intraday : ForecastType.Production,
        missingForecastIntervalsPerQueue,
        missingOrIncompleteForecastQueueIds,
        isLoadingForecast: false,
        schedulingChartState:
          state.isCalculatingWorkContributionData ||
          state.isLoadingProductionScheduleElements ||
          state.isLoadingQueuesStaffingOfProductionElements
            ? state.schedulingChartState
            : loadContributionData(),
      };
    }

    case SchedulingAction.SET_LOADING_PRODUCTION_SCHEDULE_ELEMENTS: {
      const { isLoadingProductionScheduleElements } = action.payload;
      return {
        ...state,
        isLoadingProductionScheduleElements,
      };
    }

    case SchedulingAction.RESTORE_PRODUCTION_SCHEDULE_ELEMENTS: {
      const {
        missingQueuesData,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
      } = action.payload;

      const { chartState, scheduleEndTime, scheduleStartTime, visibleAgentIdsInViewPort, visibleSupervisedAgentIds } =
        state;

      const productionScheduleElementsWithinCurrentDay = getScheduleElementsForDateRange({
        startTimestamp: scheduleStartTime,
        endTimestamp: scheduleEndTime,
        scheduleElements: getAgentElementsWithCorrectPosition(productionScheduleElements, visibleSupervisedAgentIds),
      });

      const usedProductionElementsForGantt = productionScheduleElementsWithinCurrentDay.filter(
        ({ agentId }) => visibleAgentIdsInViewPort[agentId]
      );

      const scheduleStart = (chartState.xAxis as XAxisOptions[])[0].min!;
      const scheduleEnd = (chartState.xAxis as XAxisOptions[])[0].max!;

      return {
        ...state,
        productionScheduleElements,
        productionScheduleElementsCoverage,
        productionScheduleElementsMissingQueueIds,
        productionScheduleElementsWithinCurrentDay,
        isLoadingProductionScheduleElements: false,
        chartState: {
          ...chartState,
          series: [
            (chartState.series as SeriesOptionsType[])[0],
            (chartState.series as SeriesOptionsType[])[1],
            {
              ...(chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(usedProductionElementsForGantt),
            },
          ] as SeriesOptionsType[],
        },
        /**
         * In case of no change to schedule was provided by user, pre-calculated contribution data should be used!
         * This is not valid in case of intraday forecast is being used as source data, due to BE doesn't restore calculated
         * intraday queues staffing as these data are not useful for them yet - we need to calculate Work contribution manually.
         */
        schedulingChartState:
          state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production
            ? mapInitialChartData({
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                initialStaffingPerQueue: state.initialQueuesStaffingData,
                queueIds: state.queueIds,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
              })
            : mapChartData({
                agents: state.agents,
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                schedulingChartType: state.schedulingChartType,
                productionElementsWorkContribution: state.productionElementsWorkContributionData,
                productionScheduleElements: productionScheduleElementsWithinCurrentDay,
                productionScheduleElementsMissingQueueIds: productionScheduleElementsMissingQueueIds,
                productiveElementIds: state.productiveElementsIds,
                queueIds: state.queueIds,
                queues: state.queues,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                scheduleElements: state.usedScheduleElementsWithinCurrentDay,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
                workContributionData: state.workContributionData,
              }),
        nearestSurroundingDaysProductionElements: findNearestElementsWithinSurroundingDays(
          productionScheduleElements,
          scheduleStart,
          scheduleEnd
        ),
        queues: { ...state.queues, ...(missingQueuesData as QueuesWithColors) },
      };
    }

    case SchedulingAction.RESOLVE_CONFLICTS_ON_ELEMENTS: {
      const { productionElementsInConflict } = action.payload;
      const { chartState, usedScheduleElements, visibleAgentIdsInViewPort } = state;

      const scheduleStart = (chartState.xAxis as XAxisOptions[])[0].min!;
      const scheduleEnd = (chartState.xAxis as XAxisOptions[])[0].max!;

      const productionElementsInConflictPerAgent = productionElementsInConflict.reduce(
        (elements, element) => ({
          ...elements,
          [element.agentId]: [...(elements[element.agentId] ?? []), element],
        }),
        {} as Record<string, ProductionChartScheduleElement[]>
      );

      const usedScheduleElementsUpdated = usedScheduleElements.filter(
        (element) =>
          !productionElementsInConflictPerAgent[element.agentId] ||
          productionElementsInConflictPerAgent[element.agentId].every(
            (productionElement) =>
              !areIntervalsOverlapping(
                { start: element.start, end: element.end },
                { start: productionElement.start, end: productionElement.end }
              )
          )
      );

      const usedScheduleElementsWithinCurrentDayUpdated = getScheduleElementsForDateRange({
        startTimestamp: scheduleStart,
        endTimestamp: scheduleEnd,
        scheduleElements: getAgentElementsWithCorrectPosition(
          usedScheduleElementsUpdated,
          state.visibleSupervisedAgentIds
        ),
      });

      const usedScheduleElementsForGantt = usedScheduleElementsWithinCurrentDayUpdated.filter(
        ({ agentId }) => visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        chartState: {
          ...chartState,
          series: [
            {
              ...(chartState.series as SeriesOptionsType[])[0],
              data: usedScheduleElementsForGantt,
            },
            (chartState.series as SeriesOptionsType[])[1],
            (chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        // precalculated queues staffing cannot be applied, need to recalculate contributions manually
        schedulingChartState: mapChartData({
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          agents: state.agents,
          endTimestamp: scheduleEnd,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: usedScheduleElementsWithinCurrentDayUpdated,
          startTimestamp: scheduleStart,
          workContributionData: state.workContributionData,
        }),
        usedScheduleElements: usedScheduleElementsUpdated,
        usedScheduleElementsWithinCurrentDay: usedScheduleElementsWithinCurrentDayUpdated,
        usePrecalculatedQueuesStaffing: false,
      };
    }

    case SchedulingAction.SET_LOCKED_AGENTS_IDS: {
      const { lockedAgentsIds } = action.payload;

      return {
        ...state,
        lockedAgentsIds,
      };
    }

    case SchedulingAction.CHANGE_TIMEZONE: {
      const { maxDate, minDate, timezone } = action.payload;

      /**
       * clipboard for copy/paste should be reset, as in new timezone,
       * it wouldn't cover all elements of previewed day
       */
      const emptyClipboard = {
        isPasteScheduleActive: false,
        copiedScheduleElements: [],
        agentDaySettings: {},
      };

      const scheduleElementsOfUpdatedDateRange = getScheduleElementsForDateRange({
        startTimestamp: minDate,
        endTimestamp: maxDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          state.usedScheduleElements,
          state.visibleSupervisedAgentIds
        ),
      }).map(unselectElement);

      const ganttElementsData = scheduleElementsOfUpdatedDateRange.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      const numberOfHoursInFollowingSchedule = differenceInHours(maxDate, minDate);
      const updatedChartWidth = CHART_HOUR_INTERVAL_WIDTH * numberOfHoursInFollowingSchedule;

      const productionScheduleElementsWithinCurrentDayUpdated = getScheduleElementsForDateRange({
        startTimestamp: minDate,
        endTimestamp: maxDate,
        scheduleElements: getAgentElementsWithCorrectPosition(
          state.productionScheduleElements,
          state.visibleSupervisedAgentIds
        ),
      });

      const productionScheduleElementsForGantt = productionScheduleElementsWithinCurrentDayUpdated.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        clipboard: emptyClipboard,
        chartState: {
          ...state.chartState,
          chart: {
            ...state.chartState.chart,
            width: updatedChartWidth,
          },
          series: [
            {
              // data need to be recalculated, as some of previous elements within gantt could have moved to surrounding days
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[1],
              data: mapNotAssignedAgentSeriesData(
                state.notAssignedAgentIdsWithScheduleElement,
                state.visibleSupervisedAgentIds,
                minDate,
                maxDate
              ),
            },
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionScheduleElementsForGantt),
            },
          ] as SeriesOptionsType[],
          xAxis: [
            {
              ...(state.chartState.xAxis as XAxisOptions[])[0],
              min: minDate,
              max: maxDate,
            },
          ],
        },
        nearestSurroundingDaysElements: findNearestElementsWithinSurroundingDays(
          state.usedScheduleElements,
          minDate,
          maxDate
        ),
        pinnedScheduleElementsIds: getElementsKeysWithFlag(scheduleElementsOfUpdatedDateRange, 'pinned'),
        productionScheduleElementsWithinCurrentDay: productionScheduleElementsWithinCurrentDayUpdated,
        selectedScheduleElementsIds: [],
        viewedTimezone: timezone,
        usedScheduleElementsWithinCurrentDay: scheduleElementsOfUpdatedDateRange,
      };
    }

    case SchedulingAction.TOGGLE_SELECT_SCHEDULE_ELEMENTS_STATE: {
      const { preserveOtherElementsState, scheduleElementIds } = action.payload;

      const scheduleElementsWithUpdatedSelect = state.usedScheduleElementsWithinCurrentDay.map((element) => {
        if (scheduleElementIds.includes(element.key)) {
          return { ...element, selected: !element.selected };
        }

        if (!preserveOtherElementsState) {
          return {
            ...element,
            selected: false,
          };
        }

        return element;
      });

      const selectedScheduleElementsIds = getElementsKeysWithFlag(scheduleElementsWithUpdatedSelect, 'selected');

      const ganttElementsData = scheduleElementsWithUpdatedSelect.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        selectedScheduleElementsIds,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        usedScheduleElementsWithinCurrentDay: scheduleElementsWithUpdatedSelect,
        schedulingAction: SchedulingAction.TOGGLE_SELECT_SCHEDULE_ELEMENTS_STATE,
      };
    }

    case SchedulingAction.PIN_SELECTED_ELEMENTS: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');

      const elementIdsToPin = agentId
        ? state.selectedScheduleElementsIds.filter(
            (elementId) =>
              usedScheduleElements[elementId]?.agentId === agentId && usedScheduleElements[elementId]?.pinnable
          )
        : state.selectedScheduleElementsIds.filter((elementId) => usedScheduleElements[elementId]?.pinnable);

      const updatedUsedScheduleElements = state.usedScheduleElements.map((element) => {
        if (elementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const updatedCurrentDaysElements = state.usedScheduleElementsWithinCurrentDay.map((element) => {
        if (elementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: union(state.pinnedScheduleElementsIds, elementIdsToPin),
        pinnedUsedScheduleElementsIds: union(state.pinnedUsedScheduleElementsIds, elementIdsToPin),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.PIN_SELECTED_ELEMENTS,
      };
    }

    case SchedulingAction.PIN_ELEMENTS_IN_CURRENT_DAY: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');
      const currentDaysElements = state.usedScheduleElementsWithinCurrentDay;

      const elementsToPin = agentId
        ? currentDaysElements.filter(
            ({ key }) => usedScheduleElements[key]?.agentId === agentId && usedScheduleElements[key]?.pinnable
          )
        : currentDaysElements.filter(({ key }) => usedScheduleElements[key]?.pinnable);

      const elementIdsToPin = elementsToPin.map(({ key }) => key);

      const updatedUsedScheduleElements = state.usedScheduleElements.map((element) => {
        if (elementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const updatedCurrentDaysElements = currentDaysElements.map((element) => {
        if (elementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: union(state.pinnedScheduleElementsIds, elementIdsToPin),
        pinnedUsedScheduleElementsIds: union(state.pinnedUsedScheduleElementsIds, elementIdsToPin),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.PIN_ELEMENTS_IN_CURRENT_DAY,
      };
    }

    case SchedulingAction.PIN_ALL_SCHEDULE_ELEMENTS: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');
      const currentDaysElements = state.usedScheduleElementsWithinCurrentDay;
      const currentDaysElementsRecord = recordBy(currentDaysElements, 'key');

      const elementsToPin = agentId
        ? state.usedScheduleElements.filter(
            ({ key }: ChartScheduleElement) =>
              usedScheduleElements[key]?.agentId === agentId && usedScheduleElements[key]?.pinnable
          )
        : state.usedScheduleElements.filter(({ key }: ChartScheduleElement) => usedScheduleElements[key]?.pinnable);

      const elementIdsToPin = elementsToPin.map(({ key }: ChartScheduleElement) => key);

      const inChartElementIdsToPin = elementIdsToPin.filter((elementId) => currentDaysElementsRecord[elementId]);

      const updatedUsedScheduleElements = state.usedScheduleElements.map((element) => {
        if (elementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const updatedCurrentDaysElements = currentDaysElements.map((element) => {
        if (inChartElementIdsToPin.includes(element.key)) {
          return {
            ...element,
            pinned: true,
          };
        }

        return element;
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: union(state.pinnedScheduleElementsIds, inChartElementIdsToPin),
        pinnedUsedScheduleElementsIds: union(state.pinnedUsedScheduleElementsIds, elementIdsToPin),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.PIN_ALL_SCHEDULE_ELEMENTS,
      };
    }

    case SchedulingAction.UNPIN_SELECTED_ELEMENTS: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');

      const elementIdsToUnpin = agentId
        ? state.selectedScheduleElementsIds.filter((elementId) => usedScheduleElements[elementId]?.agentId === agentId)
        : state.selectedScheduleElementsIds;

      const updatedUsedScheduleElements = state.usedScheduleElements.map((element) => {
        if (elementIdsToUnpin.includes(element.key)) {
          return {
            ...element,
            pinned: false,
          };
        }

        return element;
      });

      const updatedCurrentDaysElements = state.usedScheduleElementsWithinCurrentDay.map((element: any) => {
        if (elementIdsToUnpin.includes(element.key)) {
          return {
            ...element,
            pinned: false,
          };
        }

        return element;
      });

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: difference(state.pinnedScheduleElementsIds, elementIdsToUnpin),
        pinnedUsedScheduleElementsIds: difference(state.pinnedUsedScheduleElementsIds, elementIdsToUnpin),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.UNPIN_SELECTED_ELEMENTS,
      };
    }

    case SchedulingAction.UNPIN_ELEMENTS_IN_CURRENT_DAY: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');
      const currentDaysElements = state.usedScheduleElementsWithinCurrentDay;

      const elementsToUnpin = agentId
        ? currentDaysElements.filter(({ pinnable, key }) => pinnable && usedScheduleElements[key]?.agentId === agentId)
        : currentDaysElements.filter(({ pinnable }) => pinnable);

      const elementIdsToUnpin = elementsToUnpin.map(({ key }) => key);

      const updatedUsedScheduleElements = state.usedScheduleElements.map((element) => {
        if (elementIdsToUnpin.includes(element.key)) {
          return {
            ...element,
            pinned: false,
          };
        }

        return element;
      });

      const updatedCurrentDaysElements = agentId
        ? currentDaysElements.map((element) => {
            if (elementIdsToUnpin.includes(element.key)) {
              return {
                ...element,
                pinned: false,
              };
            }

            return element;
          })
        : currentDaysElements.map((element) => ({
            ...element,
            pinned: false,
          }));

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        usedScheduleElements: updatedUsedScheduleElements,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: agentId ? difference(state.pinnedScheduleElementsIds, elementIdsToUnpin) : [],
        pinnedUsedScheduleElementsIds: difference(state.pinnedUsedScheduleElementsIds, elementIdsToUnpin),
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.UNPIN_ELEMENTS_IN_CURRENT_DAY,
      };
    }

    case SchedulingAction.UNPIN_ALL_SCHEDULE_ELEMENTS: {
      const { agentId } = action.payload;

      const usedScheduleElements = recordBy(state.usedScheduleElements, 'key');
      const currentDaysElements = state.usedScheduleElementsWithinCurrentDay;
      const currentDaysElementsRecord = recordBy(currentDaysElements, 'key');

      const elementsToUnpin = agentId
        ? state.usedScheduleElements.filter(
            ({ key, pinnable }) => pinnable && usedScheduleElements[key]?.agentId === agentId
          )
        : state.usedScheduleElements.filter(({ pinnable }) => pinnable);

      const elementIdsToUnpin = elementsToUnpin.map(({ key }) => key);

      const inChartElementIdsToUnpin = elementIdsToUnpin.filter((elementId) => currentDaysElementsRecord[elementId]);

      const updatedUsedScheduleElements = agentId
        ? state.usedScheduleElements.map((element) => {
            if (elementIdsToUnpin.includes(element.key)) {
              return {
                ...element,
                pinned: false,
              };
            }

            return element;
          })
        : state.usedScheduleElements.map((element) => ({ ...element, pinned: false }));

      const updatedCurrentDaysElements = agentId
        ? currentDaysElements.map((element) => {
            if (inChartElementIdsToUnpin.includes(element.key)) {
              return {
                ...element,
                pinned: false,
              };
            }

            return element;
          })
        : currentDaysElements.map((element) => ({ ...element, pinned: false }));

      const ganttElementsData = updatedCurrentDaysElements.filter(
        ({ agentId }) => state.visibleAgentIdsInViewPort[agentId]
      );

      return {
        ...state,
        isDirty: true,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttElementsData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            (state.chartState.series as SeriesOptionsType[])[2],
          ] as SeriesOptionsType[],
        },
        pinnedScheduleElementsIds: agentId ? difference(state.pinnedScheduleElementsIds, inChartElementIdsToUnpin) : [],
        pinnedUsedScheduleElementsIds: agentId
          ? difference(state.pinnedUsedScheduleElementsIds, elementIdsToUnpin)
          : [],
        usedScheduleElements: updatedUsedScheduleElements,
        usedScheduleElementsWithinCurrentDay: updatedCurrentDaysElements,
        schedulingAction: SchedulingAction.UNPIN_ALL_SCHEDULE_ELEMENTS,
      };
    }

    case SchedulingAction.SET_AUTO_SCHEDULE_OPTIONS: {
      const { optionKey, optionValue } = action.payload;

      return {
        ...state,
        autoScheduleOptions: {
          ...state.autoScheduleOptions,
          [optionKey]: optionValue,
        },
      };
    }

    case SchedulingAction.SET_AUTO_SCHEDULE_SELECTED_RANGE: {
      const { rangeKey, value } = action.payload;

      return {
        ...state,
        autoScheduleSelectedRange: {
          ...state.autoScheduleSelectedRange,
          [rangeKey]: value,
        },
      };
    }

    case SchedulingAction.RESET_AUTO_SCHEDULE_OPTIONS_AND_SELECTED_RANGE: {
      const { endDateTime, startDateTime } = action.payload;

      return {
        ...state,
        autoScheduleOptions: { ...initialSchedulingState.autoScheduleOptions },
        autoScheduleSelectedRange: {
          endDateTime,
          startDateTime,
          strictSelectedRange: state.schedulingChartType === SchedulingChartType.Intraday,
        },
      };
    }

    case SchedulingAction.RELOAD_CHART_DATA: {
      const { agentIds } = action.payload;

      const agentIdsRecord = recordFromIds(agentIds, true);

      if (equals(agentIdsRecord, state.visibleAgentIdsInViewPort)) {
        return state;
      }

      const ganttChartData = state.usedScheduleElementsWithinCurrentDay.filter(
        ({ agentId }) => agentIdsRecord[agentId]
      );

      const productionElementsUpdated = state.productionScheduleElementsWithinCurrentDay.filter(
        ({ agentId }) => agentIdsRecord[agentId]
      );

      return {
        ...state,
        chartState: {
          ...state.chartState,
          series: [
            {
              ...(state.chartState.series as SeriesOptionsType[])[0],
              data: ganttChartData,
            },
            (state.chartState.series as SeriesOptionsType[])[1],
            {
              ...(state.chartState.series as SeriesOptionsType[])[2],
              data: mapProductionScheduleData(productionElementsUpdated),
            },
          ] as SeriesOptionsType[],
        },
        visibleAgentIdsInViewPort: agentIdsRecord,
      };
    }

    case SchedulingAction.LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING: {
      return {
        ...state,
        isCalculatingWorkContributionOfProductionElements: false,
        isLoadingQueuesStaffingOfProductionElements: true,
      };
    }

    case SchedulingAction.LOAD_PRODUCTION_ELEMENTS_QUEUES_STAFFING_FAILED: {
      const loadContributionData = () => {
        /**
         * In case of no change to schedule was provided by user, pre-calculated contribution data should be used!
         * This is not valid in case of intraday forecast is being used as source data, due to BE doesn't restore calculated
         * intraday queues staffing as these data are not useful for them yet - we need to calculate Work contribution manually.
         */
        if (state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production) {
          return mapInitialChartData({
            endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
            forecast: state.forecast,
            initialStaffingPerQueue: state.initialQueuesStaffingData,
            queueIds: state.queueIds,
            queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
            startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          });
        }

        return mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
          scheduleElements: state.usedScheduleElementsWithinCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        });
      };

      return {
        ...state,
        isLoadingQueuesStaffingOfProductionElements: false,
        schedulingChartState:
          state.isCalculatingWorkContributionData ||
          state.isLoadingForecast ||
          state.isLoadingProductionScheduleElements
            ? state.schedulingChartState
            : loadContributionData(),
      };
    }

    case SchedulingAction.RESTORE_PRODUCTION_ELEMENTS_QUEUES_STAFFING: {
      const { productionElementsQueuesStaffing } = action.payload;

      const loadContributionData = () => {
        /**
         * In case of no change to schedule was provided by user, pre-calculated contribution data should be used!
         * This is not valid in case of intraday forecast is being used as source data, due to BE doesn't restore calculated
         * intraday queues staffing as these data are not useful for them yet - we need to calculate Work contribution manually.
         */
        if (state.usePrecalculatedQueuesStaffing && state.schedulingChartType === SchedulingChartType.Production) {
          return mapInitialChartData({
            endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
            forecast: state.forecast,
            initialStaffingPerQueue: state.initialQueuesStaffingData,
            queueIds: state.queueIds,
            queuesStaffingOfProductionElements: productionElementsQueuesStaffing,
            startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          });
        }

        return mapChartData({
          agents: state.agents,
          endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
          forecast: state.forecast,
          schedulingChartType: state.schedulingChartType,
          productionElementsWorkContribution: state.productionElementsWorkContributionData,
          productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
          productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
          productiveElementIds: state.productiveElementsIds,
          queueIds: state.queueIds,
          queues: state.queues,
          queuesStaffingOfProductionElements: productionElementsQueuesStaffing,
          scheduleElements: state.usedScheduleElementsWithinCurrentDay,
          startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
          workContributionData: state.workContributionData,
        });
      };

      return {
        ...state,
        isLoadingQueuesStaffingOfProductionElements: false,
        queuesStaffingOfProductionElementsData: productionElementsQueuesStaffing,
        schedulingChartState:
          state.isCalculatingWorkContributionData ||
          state.isLoadingForecast ||
          state.isLoadingProductionScheduleElements
            ? state.schedulingChartState
            : loadContributionData(),
      };
    }

    case SchedulingAction.CHANGE_SOURCE_OF_FORECAST: {
      const { forecastDataType, schedulingChartType } = action.payload;

      const skipLoadingProductionForecast =
        state.schedulingChartType === SchedulingChartType.SLA && schedulingChartType === SchedulingChartType.Production;

      return {
        ...state,
        forecastDataType: forecastDataType ?? state.forecastDataType, // this should be use to replace the previous forecast type only when user selects intraday forecast view, but the view needs to be reverted back to production forecast as some intraday forecast is missing
        schedulingChartType,
        ...((
          [SchedulingChartType.Intraday, SchedulingChartType.Production] as Values<typeof SchedulingChartType>[]
        ).includes(schedulingChartType) &&
          !skipLoadingProductionForecast && {
            isLoadingForecast: true,
          }),
        ...(schedulingChartType === SchedulingChartType.SLA && {
          isLoadingEstimatedServiceLevel: true,
        }),
        autoScheduleSelectedRange: {
          ...state.autoScheduleSelectedRange,
          strictSelectedRange: schedulingChartType === SchedulingChartType.Intraday,
        },
        schedulingAction: SchedulingAction.CHANGE_SOURCE_OF_FORECAST,
      };
    }

    case SchedulingAction.LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION: {
      return {
        ...state,
        isCalculatingWorkContributionOfProductionElements: true,
        isLoadingQueuesStaffingOfProductionElements: false,
      };
    }

    case SchedulingAction.LOAD_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION_FAILED: {
      return {
        ...state,
        isCalculatingWorkContributionOfProductionElements: false,
        schedulingChartState:
          state.isCalculatingWorkContributionData ||
          state.isLoadingForecast ||
          state.isLoadingProductionScheduleElements
            ? state.schedulingChartState
            : mapChartData({
                agents: state.agents,
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                schedulingChartType: state.schedulingChartType,
                productionElementsWorkContribution: state.productionElementsWorkContributionData,
                productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
                productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
                productiveElementIds: state.productiveElementsIds,
                queueIds: state.queueIds,
                queues: state.queues,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                scheduleElements: state.usedScheduleElementsWithinCurrentDay,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
                workContributionData: state.workContributionData,
              }),
      };
    }

    case SchedulingAction.CALCULATE_PRODUCTION_ELEMENTS_WORK_CONTRIBUTION: {
      const { productionElementsWorkContribution } = action.payload;

      return {
        ...state,
        isCalculatingWorkContributionOfProductionElements: false,
        productionElementsWorkContributionData: productionElementsWorkContribution,
        schedulingChartState:
          state.isCalculatingWorkContributionData ||
          state.isLoadingForecast ||
          state.isLoadingProductionScheduleElements
            ? state.schedulingChartState
            : mapChartData({
                productionElementsWorkContribution,
                agents: state.agents,
                endTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].max!,
                forecast: state.forecast,
                schedulingChartType: state.schedulingChartType,
                productionScheduleElements: state.productionScheduleElementsWithinCurrentDay,
                productionScheduleElementsMissingQueueIds: state.productionScheduleElementsMissingQueueIds,
                productiveElementIds: state.productiveElementsIds,
                queueIds: state.queueIds,
                queues: state.queues,
                queuesStaffingOfProductionElements: state.queuesStaffingOfProductionElementsData,
                scheduleElements: state.usedScheduleElementsWithinCurrentDay,
                startTimestamp: (state.chartState.xAxis as XAxisOptions[])[0].min!,
                workContributionData: state.workContributionData,
              }),
      };
    }

    case SchedulingAction.LOAD_ESTIMATED_SERVICE_LEVEL: {
      return {
        ...state,
        isLoadingEstimatedServiceLevel: true,
      };
    }

    case SchedulingAction.LOAD_ESTIMATED_SERVICE_LEVEL_FAILED: {
      return {
        ...state,
        isLoadingEstimatedServiceLevel: false,
        estimatedServiceLevelPerQueue: undefined,
      };
    }

    case SchedulingAction.CALCULATE_ESTIMATED_SERVICE_LEVEL: {
      const { estimatedServiceLevelPerQueue } = action.payload;

      return {
        ...state,
        estimatedServiceLevelPerQueue,
        isLoadingEstimatedServiceLevel: false,
      };
    }

    default:
      return state;
  }
};
